// pdp-product.js — buy box behaviour for sections/pdp-main-product.liquid (module).

const cartAddUrl = () => `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';

async function findCart() {
  const pick = () => {
    const el = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    return el && typeof el.renderContents === 'function' ? el : null;
  };
  let el = pick();
  if (el) return el;
  // Shrine registers its cart elements after load; give it a moment before falling back.
  const names = ['cart-drawer', 'cart-notification'].filter((n) => document.querySelector(n));
  if (!names.length) return null;
  try {
    await Promise.race([
      Promise.any(names.map((n) => customElements.whenDefined(n))),
      new Promise((_, reject) => setTimeout(reject, 1500)),
    ]);
  } catch (e) {
    /* timed out */
  }
  return pick();
}

// <pdp-buy-box data-section="id" data-update-url>
//   radios [data-pdp-tier-input] (name="id", form="…") carry data-variant-id, data-qty, data-price,
//   data-compare, data-installment, data-media-id, data-available, data-url, data-title.
// Public API (used by pdp-agent.js): options(), selectVariant(id), add().
class PdpBuyBox extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.pdp') || document;
    this.form = this.section.querySelector('form.pdp-form') || this.querySelector('form');
    if (!this.form) return;
    this.idInput = this.form.querySelector('input[name="id"]');
    this.qtyInput = this.form.querySelector('input[name="quantity"]');
    this.submitBtn = this.form.querySelector('[type="submit"]');
    this.errorEl = this.querySelector('[data-pdp-error]');

    this.onSubmit = (e) => this.submit(e);
    this.onChange = (e) => {
      if (e.target.matches('[data-pdp-tier-input]')) this.select(e.target);
      if (e.target.matches('[data-pdp-variant-select]')) this.selectOption(e.target);
    };
    this.onBlockSelect = (e) => {
      const tier = e.target.closest && e.target.closest('[data-pdp-tier]');
      if (!tier || !this.contains(tier)) return;
      const input = tier.querySelector('[data-pdp-tier-input]');
      if (input && !input.disabled) {
        input.checked = true;
        this.select(input, true);
      }
    };
    // Kaching Bundles app block in place of the tier blocks: Kaching rewrites the /cart/add
    // request to the bundle the shopper picked, so add() needs nothing extra. Its
    // variants-changed event carries that bundle's quantities and prices. Kaching often loads
    // first, so the section keeps its first report in window.pdpBundle.
    this.onBundle = (e) => {
      if (e.target.closest && e.target.closest('kaching-bundles-block')) this.applyBundle(e.detail || {});
    };
    this.form.addEventListener('submit', this.onSubmit);
    this.addEventListener('change', this.onChange);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    this.section.addEventListener('variants-changed', this.onBundle);

    const checked = this.querySelector('[data-pdp-tier-input]:checked');
    if (checked) this.select(checked, true);
    if (window.pdpBundle) this.applyBundle(window.pdpBundle);
  }

  disconnectedCallback() {
    if (!this.form) return;
    this.form.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('change', this.onChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.section.removeEventListener('variants-changed', this.onBundle);
  }

  select(input, initial = false) {
    const d = input.dataset;
    if (this.idInput) this.idInput.value = d.variantId;
    if (this.qtyInput) this.qtyInput.value = d.qty || '1';
    this.querySelectorAll('[data-pdp-tier]').forEach((t) => t.classList.toggle('is-selected', t.contains(input)));
    this.applyPrices(d);
    this.setAvailable(d.available !== 'false');
    this.section.dispatchEvent(new CustomEvent('pdp:variant-change', { bubbles: true, detail: { variantId: d.variantId, mediaId: d.mediaId, initial } }));
    if (!initial && this.hasAttribute('data-update-url') && d.url) window.history.replaceState({}, '', d.url);
  }

  // Plain variant dropdown fallback (when no tier blocks are used).
  selectOption(select) {
    const opt = select.selectedOptions[0];
    if (opt) this.select(opt);
  }

  applyPrices(d) {
    const set = (sel, text, hideWhenEmpty) => {
      this.section.querySelectorAll(sel).forEach((el) => {
        if (text === undefined) return;
        // Elements with a visually hidden label keep it: the price goes into [data-pdp-text].
        (el.querySelector('[data-pdp-text]') || el).textContent = text;
        if (hideWhenEmpty) el.hidden = !text;
      });
    };
    set('[data-pdp-price-current]', d.price);
    set('[data-pdp-price-compare]', d.compare, true);
    set('[data-pdp-installment]', d.installment);
    set('[data-pdp-price-off]', d.off, true);
  }

  // The bundle picked in the Kaching block: its quantity, and its totals (amounts in cents,
  // already formatted in the shopper's currency). A trailing .00 is dropped, as in the
  // prices the theme prints.
  applyBundle(d) {
    this.bundleQty = (d.variantIdQuantities || []).reduce((n, v) => n + (Number(v.quantity) || 0), 0) || 1;
    const now = d.pricing && d.pricing.discountedPrice;
    if (!now || !now.formatted) return;
    const was = d.pricing.fullPrice;
    const trim = (s) => String(s).replace(/\.00(?!\d)/, '');
    const fmt = (cents) => trim(now.formatted.replace(/\d[\d,.]*/, (cents / 100).toFixed(2)));
    this.applyPrices({
      price: trim(now.formatted),
      compare: was && was.amount > now.amount ? trim(was.formatted) : '',
      installment: fmt(Math.round(now.amount / 4)),
    });
  }

  setAvailable(ok) {
    if (!this.submitBtn) return;
    this.submitBtn.toggleAttribute('disabled', !ok);
    this.submitBtn.classList.toggle('is-sold-out', !ok);
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg || '';
    this.errorEl.hidden = !msg;
  }

  setLoading(on) {
    this.submitBtn && this.submitBtn.classList.toggle('is-loading', on);
    this.submitBtn && this.submitBtn.setAttribute('aria-busy', String(on));
    document.querySelectorAll('[data-pdp-sticky-btn]').forEach((b) => b.classList.toggle('is-loading', on));
  }

  // Every purchasable option on the page, in display order.
  options() {
    const fromTiers = [...this.querySelectorAll('[data-pdp-tier-input]')].map((i) => ({
      variant_id: i.dataset.variantId,
      title: i.dataset.title || '',
      quantity: Number(i.dataset.qty || 1),
      price: i.dataset.price,
      compare_at_price: i.dataset.compare || null,
      available: i.dataset.available !== 'false' && !i.disabled,
      selected: i.checked,
    }));
    if (fromTiers.length) return fromTiers;
    const sel = this.querySelector('[data-pdp-variant-select]');
    if (sel) {
      return [...sel.options].map((o) => ({
        variant_id: o.value,
        title: o.textContent.trim(),
        quantity: 1,
        price: o.dataset.price,
        compare_at_price: o.dataset.compare || null,
        available: !o.disabled,
        selected: o.selected,
      }));
    }
    // Plain form (a bundle app picks the bundle): the one variant, at the bundle's quantity.
    if (!this.idInput) return [];
    const text = (s) => {
      const el = this.section.querySelector(s);
      return el ? (el.querySelector('[data-pdp-text]') || el).textContent.trim() : '';
    };
    return [{
      variant_id: this.idInput.value,
      title: '',
      quantity: this.bundleQty || Number((this.qtyInput && this.qtyInput.value) || 1),
      price: text('[data-pdp-price-current]'),
      compare_at_price: text('[data-pdp-price-compare]') || null,
      available: !(this.submitBtn && this.submitBtn.disabled),
      selected: true,
    }];
  }

  // Select the option with this variant id. Returns false if there is no such available option.
  selectVariant(id) {
    const key = CSS.escape(String(id));
    const input = this.querySelector(`[data-pdp-tier-input][data-variant-id="${key}"]`);
    if (input && !input.disabled) {
      input.checked = true;
      this.select(input);
      return true;
    }
    const sel = this.querySelector('[data-pdp-variant-select]');
    const opt = sel && [...sel.options].find((o) => o.value === String(id) && !o.disabled);
    if (opt) {
      sel.value = opt.value;
      this.select(opt);
      return true;
    }
    if (!sel && !this.querySelector('[data-pdp-tier-input]') && this.idInput && this.idInput.value === String(id)) {
      return !(this.submitBtn && this.submitBtn.disabled);
    }
    return false;
  }

  async submit(e) {
    e.preventDefault();
    try {
      await this.add();
    } catch (err) {
      /* shown in the error line */
    }
  }

  // Adds the current selection to the cart and opens Shrine's cart drawer. Resolves with the
  // cart response; rejects with an Error whose message is what the shopper sees.
  async add() {
    if (!this.submitBtn || this.submitBtn.disabled) throw new Error('This option is sold out.');
    if (this.submitBtn.classList.contains('is-loading')) throw new Error('Already adding to cart.');
    this.setLoading(true);
    this.showError('');
    const fd = new FormData(this.form);
    const selected = this.querySelector('[data-pdp-tier].is-selected');
    const gifts = selected ? [...selected.querySelectorAll('[data-pdp-gift-variant]')].map((g) => g.dataset.pdpGiftVariant).filter(Boolean) : [];
    if (gifts.length) {
      const id = fd.get('id');
      const qty = fd.get('quantity') || '1';
      fd.delete('id');
      fd.delete('quantity');
      fd.append('items[0][id]', id);
      fd.append('items[0][quantity]', qty);
      gifts.forEach((g, i) => {
        fd.append(`items[${i + 1}][id]`, g);
        fd.append(`items[${i + 1}][quantity]`, '1');
      });
    }
    const cart = await findCart();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      fd.append('sections', cart.getSectionsToRender().map((s) => s.section || s.id));
      fd.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
    }
    let data;
    try {
      const res = await fetch(cartAddUrl(), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd,
      });
      data = await res.json();
      if (!res.ok || data.status) {
        const msg = data.description || data.message || 'Something went wrong. Please try again.';
        this.showError(msg);
        throw new Error(msg);
      }
    } catch (err) {
      this.setLoading(false);
      if (data) throw err;
      console.error(err);
      window.location = cartUrl();
      throw err;
    }
    if (!cart) {
      window.location = cartUrl();
      this.setLoading(false);
      return data;
    }
    if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
    const payload = data.items && !data.key ? { ...data.items[0], sections: data.sections } : data;
    cart.renderContents(payload);
    // No cartUpdate publish here: Shrine's drawer items answer it by loading the cart page's
    // item list into the drawer, replacing what renderContents just drew.
    document.dispatchEvent(new CustomEvent('pdp:cart-added', { detail: data }));
    this.setLoading(false);
    return data;
  }
}

// <pdp-gallery data-slider="MainSliderId" [data-follow="true"]> shows the selected variant's
// image on load. Picking a bundle afterwards leaves the gallery where it is (as on the
// original) unless data-follow="true".
class PdpGallery extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.pdp') || document;
    this.onVariant = (e) => {
      const id = e.detail && e.detail.mediaId;
      if (!id || !(e.detail.initial || this.dataset.follow === 'true')) return;
      const slider = document.getElementById(this.dataset.slider);
      if (!slider || typeof slider.goTo !== 'function') return;
      const slides = slider.slides();
      const i = slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i >= 0) slider.goTo(i, e.detail.initial ? 'auto' : 'smooth');
    };
    this.section.addEventListener('pdp:variant-change', this.onVariant);
  }
  disconnectedCallback() {
    this.section && this.section.removeEventListener('pdp:variant-change', this.onVariant);
  }
}

// Height of the store's sticky header (Shrine/Dawn <sticky-header>), 0 when it does not stick.
const headerOffset = () => {
  const sh = document.querySelector('sticky-header');
  const el = sh && (sh.closest('.shopify-section') || sh);
  if (!el || !/sticky|fixed/.test(getComputedStyle(el).position)) return 0;
  return el.getBoundingClientRect().height;
};

// <pdp-sticky-atc data-watch="ElementId" data-action="scroll|add" data-target="AnchorId"
//   data-show-after="1300" data-offset="20">
// Shows once the page is scrolled data-show-after px (as on the original; 0 = once the watched
// Add to cart button has left the screen). "Shop now" brings the product title to the top of
// the screen, data-offset px below the store's sticky header.
class PdpStickyAtc extends HTMLElement {
  connectedCallback() {
    this.watch = document.getElementById(this.dataset.watch);
    this.btn = this.querySelector('[data-pdp-sticky-btn]');
    this.sectionEl = this.closest('.shopify-section');
    if (!this.watch) return;
    // Checked on scroll (once per frame) rather than with an IntersectionObserver, which
    // misses instant jumps past the button, e.g. a scroll-to-top button.
    this.check = () => {
      this.raf = null;
      const after = parseInt(this.dataset.showAfter || '0', 10);
      this.past = after > 0 ? window.scrollY >= after : this.watch.getBoundingClientRect().bottom <= 0;
      this.render();
    };
    this.onScroll = () => {
      if (!this.raf) this.raf = requestAnimationFrame(this.check);
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    this.check();
    this.onClick = (e) => {
      e.preventDefault();
      if (this.dataset.action === 'add') {
        const form = this.watch.closest('form') || document.querySelector(`#${this.dataset.target} form`);
        form && form.requestSubmit();
        return;
      }
      const title = this.sectionEl && this.sectionEl.querySelector('.pdp-title-blk');
      const target = title || document.getElementById(this.dataset.target) || this.watch;
      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset() - parseInt(this.dataset.offset || '20', 10);
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };
    this.btn && this.btn.addEventListener('click', this.onClick);
    this.onSectionSelect = (e) => {
      if (this.sectionEl && e.target === this.sectionEl) {
        this.forced = true;
        this.render();
      }
    };
    this.onSectionDeselect = (e) => {
      if (this.sectionEl && e.target === this.sectionEl) {
        this.forced = false;
        this.render();
      }
    };
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
  }
  render() {
    const show = !!(this.past || this.forced);
    if (show === this.shown) return;
    this.shown = show;
    this.classList.toggle('is-visible', show);
    this.setAttribute('aria-hidden', String(!show));
    this.toggleAttribute('inert', !show);
    // Bottom space for the page while the bar shows, so the footer is never hidden under it.
    const root = document.documentElement;
    if (show) root.style.setProperty('--pdp-sticky-h', `${this.offsetHeight}px`);
    root.classList.toggle('pdp-sticky-on', show);
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    if (this.raf) cancelAnimationFrame(this.raf);
    document.documentElement.classList.remove('pdp-sticky-on');
    this.btn && this.btn.removeEventListener('click', this.onClick);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
  }
}

for (const [name, cls] of [
  ['pdp-buy-box', PdpBuyBox],
  ['pdp-gallery', PdpGallery],
  ['pdp-sticky-atc', PdpStickyAtc],
]) {
  if (!customElements.get(name)) customElements.define(name, cls);
}
